import React, {PropsWithChildren, ReactNode, useEffect, useMemo, useState} from 'react';
import {isFunction} from 'underscore';
import {StepperContext, StepperContextValue} from './stepper-context';
import styles from './styles.module.less';
import {join} from '@esgi/ui-kit/utils';

type Child = ReactNode;

type Props = {
	initialStep?: number;
	step?: number;
	onStepChanged?: (step: number) => void;
	onGoBack?: () => void;
	onNext?: (currentStep: number, nextStep: number) => void;
	onNextClick?: (currentStep: number, nextStep: number) => void;
	className?: string;
	children: Child | ((context: StepperContextValue) => Child);
} & PropsWithChildren;

export function VerticalStepper({onStepChanged, ...props}: Props) {
	const [activeStep, setActiveStep] = useState<number>(props.initialStep);
	const [amountOfSteps, setAmountOfSteps] = useState<number[]>([]);

	useEffect(() => {
		if (props.step !== undefined) {
			setActiveStep(props.step);
		}
	}, [props.step]);

	useEffect(() => {
		onStepChanged && onStepChanged(activeStep);
	}, [activeStep, onStepChanged]);

	const back = () => {
		if (activeStep === 0) {
			props.onGoBack?.();
			return;
		}
		setActiveStep(activeStep - 1);
	};

	const onNextClick = () => {
		props.onNextClick?.(activeStep, activeStep + 1);
	};

	const next = () => {
		if (activeStep === amountOfSteps.length - 1) {
			return;
		}
		props.onNextClick?.(activeStep, activeStep + 1);
		setActiveStep(activeStep + 1);
	};

	const contextValue = useMemo(() => {
		return {
			activeStep,
			setActiveStep,
			next,
			back,
			addStep: (step) => setAmountOfSteps([...amountOfSteps, step]),
			removeStep: (step) => setAmountOfSteps(amountOfSteps.filter(a => a !== step)),
		} as StepperContextValue;
	}, [activeStep, setActiveStep, next, back, amountOfSteps]);

	return <div className={join(styles.stepper, props.className)}>
		<StepperContext.Provider value={contextValue}>
			{isFunction(props.children) ? props.children(contextValue) : props.children}
		</StepperContext.Provider>
	</div>;
}

export * from './components/step';
